<template>
  <!--
    Pro-list desktop chrome. The page keeps its own <v-data-table> (and all its
    cell slots) and passes it as the default slot, so column plugins, $refs and
    API logic stay on the page. See docs/design-system/patterns/list-page.md.
  -->
  <div
    class="pl"
    :class="{ 'pl--fixed': fixed, 'theme--dark': $vuetify.theme.isDark }"
    :style="rootStyle"
  >
    <header class="pl-head">
      <div class="pl-head__title-block">
        <span class="pl-head__icon"><v-icon size="18">{{ icon }}</v-icon></span>
        <div>
          <h1 class="pl-head__title">{{ title }}</h1>
          <div class="pl-head__sub">
            <template v-if="total != null">
              <span class="pl-num">{{ faNum(group(total)) }}</span> {{ $t("plRecords") }}
            </template>
            <template v-if="subtitle"> · {{ subtitle }}</template>
          </div>
        </div>
      </div>

      <label v-if="searchable" class="pl-search">
        <v-icon size="18">mdi-magnify</v-icon>
        <input
          :value="search"
          type="search"
          class="pl-search__input"
          :placeholder="searchPlaceholder || $t('search')"
          autocomplete="off"
          @input="$emit('update:search', $event.target.value)"
        />
        <button
          v-if="search"
          type="button"
          class="pl-search__clear"
          :aria-label="$t('clear')"
          @click="$emit('update:search', null)"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </label>
      <span v-else class="pl-head__spacer" />

      <div class="pl-head__actions">
        <button
          v-if="filterable"
          type="button"
          class="pl-btn"
          :class="{ 'pl-btn--on': filterCount }"
          @click="$emit('filter')"
        >
          <v-icon size="16">mdi-filter-variant</v-icon>
          <span class="pl-btn__text">{{ $t("filter") }}</span>
          <span v-if="filterCount" class="pl-btn__badge">{{ faNum(filterCount) }}</span>
        </button>

        <div class="pl-btn-group">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="pl-btn pl-btn--icon"
                v-bind="attrs"
                v-shortkey="['f5']"
                v-on="on"
                @click="$emit('refresh')"
                @shortkey="$emit('refresh')"
              >
                <v-icon size="16">mdi-refresh</v-icon>
              </button>
            </template>
            <span>{{ $t("refresh") }} (F5)</span>
          </v-tooltip>
          <v-menu v-if="columns && columns.length" offset-y left :close-on-content-click="false" max-height="440">
            <template v-slot:activator="{ attrs, on }">
              <button
                type="button"
                class="pl-btn pl-btn--icon"
                :title="$t('accDocListColumns')"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="16">mdi-view-column-outline</v-icon>
              </button>
            </template>
            <v-list dense>
              <v-subheader>{{ $t("accDocListColumns") }}</v-subheader>
              <v-list-item v-for="h in columns" :key="h.value" link @click="$emit('toggle-column', h.value)">
                <v-list-item-action class="my-0 me-3">
                  <v-icon :color="hidden.includes(h.value) ? 'grey' : 'primary'">
                    {{ hidden.includes(h.value) ? "mdi-checkbox-blank-outline" : "mdi-checkbox-marked" }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ h.text }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item link :disabled="!hidden.length" @click="$emit('show-all-columns')">
                <v-list-item-title class="primary--text">{{ $t("accDocListShowAllColumns") }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- page-specific icon buttons (excel, log, …) -->
          <slot name="tools" />
        </div>

        <button
          v-if="printable"
          type="button"
          class="pl-btn"
          :class="{ 'pl-btn--primary': !createLabel }"
          :disabled="!rows"
          @click="$emit('print')"
        >
          <v-icon size="16">mdi-printer-outline</v-icon>
          <span class="pl-btn__text">{{ $t("print") }}</span>
        </button>
        <button v-if="createLabel" type="button" class="pl-btn pl-btn--primary" @click="$emit('create')">
          <v-icon size="16">mdi-plus</v-icon>
          <span class="pl-btn__text">{{ createLabel }}</span>
        </button>
      </div>
    </header>

    <!-- KPI tiles (optional) -->
    <section v-if="$slots.kpis" class="pl-kpis">
      <slot name="kpis" />
    </section>

    <!-- bulk bar when rows are selected, otherwise active filter chips -->
    <div v-if="selectedCount && $slots.bulk" class="pl-bulk">
      <span class="pl-bulk__count">
        <v-icon size="18">mdi-checkbox-multiple-marked</v-icon>
        {{ faNum(selectedCount) }} {{ $t("plSelected") }}
      </span>
      <span class="pl-sep" />
      <slot name="bulk" />
      <v-spacer />
      <button type="button" class="pl-bulk__btn" @click="$emit('clear-selection')">
        <v-icon size="17">mdi-close</v-icon>
        {{ $t("cancel") }}
      </button>
    </div>
    <div v-else-if="chips && chips.length" class="pl-chips">
      <span class="pl-chips__label">
        <v-icon size="16">mdi-filter-variant</v-icon>
        {{ $t("accDocListActiveFilters") }}
      </span>
      <span v-for="chip in chips" :key="chip.key" class="pl-chip">
        <span class="pl-chip__key">{{ chip.label }}</span>
        <span v-if="chip.value" class="pl-chip__val">{{ faNum(chip.value) }}</span>
        <button type="button" class="pl-chip__x" :aria-label="$t('clear')" @click="$emit('clear-chip', chip.key)">
          <v-icon size="14">mdi-close</v-icon>
        </button>
      </span>
      <button type="button" class="pl-link" @click="$emit('clear-filters')">
        {{ $t("accDocListClearFilters") }}
      </button>
    </div>

    <!-- page content that belongs above the table (summary rows, tabs, …) -->
    <slot name="top" />

    <div
      ref="table"
      class="pl-table"
      @mousedown="$emit('cell-down', $event)"
      @mouseover="$emit('cell-over', $event)"
    >
      <div v-if="loading" class="pl-loader" aria-busy="true">
        <div class="pl-loader__bar" />
        <div class="pl-loader__rows">
          <div v-for="n in skeletonRows" :key="n" class="pl-loader__row">
            <span class="pl-sk" style="width: 22px" />
            <span class="pl-sk" style="width: 56px" />
            <span class="pl-sk pl-sk--grow" />
            <span class="pl-sk" style="width: 80px" />
            <span class="pl-sk" style="width: 96px" />
            <span class="pl-sk" style="width: 96px" />
          </div>
        </div>
      </div>
      <slot />
    </div>

    <div v-if="cellSel" class="pl-statusbar" :class="{ 'pl-statusbar--on': cellSel.count > 1 }">
      <template v-if="cellSel.count > 1">
        <span>{{ $t("accDocListCellCount") }}: <b>{{ faNum(cellSel.count) }}</b></span>
        <template v-if="cellSel.numCount">
          <span>{{ $t("sum") }}: <b>{{ faNum($numberFormat(cellSel.sum, 10)) }}</b></span>
          <span>{{ $t("accDocListAverage") }}: <b>{{ faNum($numberFormat(cellSel.avg, 10)) }}</b></span>
        </template>
        <v-spacer />
        <button type="button" class="pl-link" @click="$emit('clear-cells')">{{ $t("clear") }}</button>
      </template>
      <template v-else>
        <v-icon size="16">mdi-gesture-tap-hold</v-icon>
        {{ $t("accDocListCellHint") }}
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProListFrame",
  props: {
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "mdi-format-list-bulleted" },
    total: { type: Number, default: null },
    rows: { type: Number, default: 0 }, // rows on the current page (enables print)
    search: { type: String, default: null },
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: "" },
    filterable: { type: Boolean, default: true },
    filterCount: { type: Number, default: 0 },
    printable: { type: Boolean, default: true },
    createLabel: { type: String, default: "" },
    columns: { type: Array, default: null }, // toggleable headers [{ text, value }]
    hidden: { type: Array, default: () => [] },
    chips: { type: Array, default: null }, // [{ key, label, value }]
    selectedCount: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    cellSel: { type: Object, default: null },
    fixed: { type: Boolean, default: true },
    skeleton: { type: Number, default: 10 },
  },
  computed: {
    rootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? "dark" : "light"] || {};
      var p = typeof t.primary === "string" ? t.primary : "#1976d2";
      return { "--pl-primary": p, "--pl-primary-soft": p + "1a" };
    },
    skeletonRows() {
      return Math.min(Math.max(this.rows, 8), Math.max(8, this.skeleton));
    },
  },
  methods: {
    faNum(v) {
      var s = String(v == null ? "" : v);
      return String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0
        ? s.replace(/[0-9]/g, function (d) { return "۰۱۲۳۴۵۶۷۸۹"[d]; })
        : s;
    },
    group(n) {
      return String(Number(n) || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/sass/design-system/list/pro-list";
.pl .pl-head__spacer {
  flex: 1 1 auto;
}
</style>
